import { useState, useEffect, useCallback, useRef } from "react";
import { Calculator as CalcIcon, X, Delete, History as HistoryIcon, Copy, Keyboard } from "lucide-react";

/**
 * GlobalCalculator
 * - Floating draggable calculator available everywhere.
 * - Global shortcut: Alt+C (toggle). Esc closes.
 * - Keyboard: 0-9, . + - * / Enter/= Backspace, % , Ctrl+C to copy result
 * - History with click-to-reuse. Persists in localStorage.
 */
export default function GlobalCalculator() {
  const [open, setOpen] = useState(false);
  const [expr, setExpr] = useState("");
  const [result, setResult] = useState("0");
  const [err, setErr] = useState("");
  const [history, setHistory] = useState(() => {
    try { return JSON.parse(localStorage.getItem("calc_history") || "[]"); } catch { return []; }
  });
  const [showHistory, setShowHistory] = useState(false);
  const [showKeys, setShowKeys] = useState(false);
  const [pos, setPos] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem("calc_pos") || "null");
      if (saved) return saved;
    } catch { /* ignore */ }
    // default to bottom-right
    return { x: Math.max(20, window.innerWidth - 340), y: Math.max(20, window.innerHeight - 520) };
  });
  const dragRef = useRef({ dragging: false, dx: 0, dy: 0 });
  const panelRef = useRef(null);

  // Persist history
  useEffect(() => {
    localStorage.setItem("calc_history", JSON.stringify(history.slice(0, 50)));
  }, [history]);

  // Persist position
  useEffect(() => {
    localStorage.setItem("calc_pos", JSON.stringify(pos));
  }, [pos]);

  // Global shortcut: Alt+C to toggle
  useEffect(() => {
    const onKey = (e) => {
      if (e.altKey && (e.key === "c" || e.key === "C")) {
        e.preventDefault();
        setOpen((o) => !o);
      } else if (e.key === "Escape" && open) {
        setOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  // Safe eval
  const evaluate = useCallback((raw) => {
    if (!raw) return { value: "0", ok: true };
    const sanitized = String(raw)
      .replace(/×/g, "*")
      .replace(/÷/g, "/")
      .replace(/−/g, "-")
      .replace(/,/g, "")
      .replace(/\s+/g, "");
    if (!/^[-+*/().%0-9]+$/.test(sanitized)) {
      return { value: raw, ok: false };
    }
    // Convert trailing-operand percentages: "50+10%" -> "50+(50*10/100)" is complex,
    // simpler: treat "N%" as "(N/100)"
    const pctd = sanitized.replace(/(\d+(\.\d+)?)%/g, "($1/100)");
    try {
      // eslint-disable-next-line no-new-func
      const v = Function(`"use strict"; return (${pctd});`)();
      if (typeof v !== "number" || !isFinite(v)) return { value: "Error", ok: false };
      const rounded = Math.round((v + Number.EPSILON) * 1e10) / 1e10;
      return { value: String(rounded), ok: true };
    } catch {
      return { value: "Error", ok: false };
    }
  }, []);

  // Live eval
  useEffect(() => {
    const { value, ok } = evaluate(expr);
    setResult(ok ? value : "—");
    setErr(!ok && expr ? "Invalid" : "");
  }, [expr, evaluate]);

  const commit = useCallback(() => {
    const { value, ok } = evaluate(expr);
    if (ok && expr) {
      setHistory((h) => [{ expr, result: value, t: Date.now() }, ...h].slice(0, 50));
      setExpr(value);
    }
  }, [evaluate, expr]);

  const append = (s) => setExpr((p) => p + s);
  const clearAll = () => { setExpr(""); setErr(""); };
  const backspace = () => setExpr((p) => p.slice(0, -1));
  const copyResult = async () => {
    try {
      await navigator.clipboard.writeText(result);
    } catch { /* ignore */ }
  };

  // Handle keyboard while open
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      const tag = (e.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea") return;
      if (e.ctrlKey && (e.key === "c" || e.key === "C")) { copyResult(); return; }
      if (/^[0-9]$/.test(e.key)) { append(e.key); e.preventDefault(); }
      else if (["+", "-", "*", "/", "(", ")", ".", "%"].includes(e.key)) { append(e.key); e.preventDefault(); }
      else if (e.key === "Enter" || e.key === "=") { commit(); e.preventDefault(); }
      else if (e.key === "Backspace") { backspace(); e.preventDefault(); }
      else if (e.key === "Delete") { clearAll(); e.preventDefault(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, commit]);

  // Drag
  const onDragStart = (e) => {
    const rect = panelRef.current?.getBoundingClientRect();
    if (!rect) return;
    dragRef.current = { dragging: true, dx: e.clientX - rect.left, dy: e.clientY - rect.top };
    e.preventDefault();
  };
  useEffect(() => {
    const onMove = (e) => {
      if (!dragRef.current.dragging) return;
      const nx = Math.max(0, Math.min(window.innerWidth - 320, e.clientX - dragRef.current.dx));
      const ny = Math.max(0, Math.min(window.innerHeight - 80, e.clientY - dragRef.current.dy));
      setPos({ x: nx, y: ny });
    };
    const onUp = () => { dragRef.current.dragging = false; };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => { window.removeEventListener("mousemove", onMove); window.removeEventListener("mouseup", onUp); };
  }, []);

  const Btn = ({ children, onClick, cls = "", testid }) => (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`h-11 rounded-md text-sm font-semibold transition active:scale-95 select-none ${cls}`}
    >{children}</button>
  );

  return (
    <>
      {/* Floating toggle button — always visible */}
      <button
        onClick={() => setOpen((o) => !o)}
        title="Calculator (Alt+C)"
        data-testid="calc-fab"
        className="fixed bottom-5 right-5 z-40 h-12 w-12 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 text-white shadow-lg hover:shadow-xl hover:scale-105 transition flex items-center justify-center"
      >
        <CalcIcon className="w-5 h-5" />
      </button>

      {open && (
        <div
          ref={panelRef}
          style={{ left: pos.x, top: pos.y }}
          data-testid="calc-panel"
          className="fixed z-50 w-[320px] bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden"
        >
          {/* Header (draggable) */}
          <div
            onMouseDown={onDragStart}
            className="flex items-center justify-between px-3 py-2 bg-gradient-to-r from-slate-800 to-slate-700 text-white cursor-move"
          >
            <div className="flex items-center gap-2">
              <CalcIcon className="w-4 h-4" />
              <span className="text-sm font-semibold">Calculator</span>
              <span className="text-[10px] opacity-60 bg-white/10 px-1.5 py-0.5 rounded">Alt+C</span>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={() => setShowKeys((v) => !v)} title="Shortcuts" className="p-1 hover:bg-white/10 rounded">
                <Keyboard className="w-3.5 h-3.5" />
              </button>
              <button onClick={() => setShowHistory((v) => !v)} title="History" className="p-1 hover:bg-white/10 rounded">
                <HistoryIcon className="w-3.5 h-3.5" />
              </button>
              <button onClick={() => setOpen(false)} className="p-1 hover:bg-white/10 rounded" data-testid="calc-close">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

          {/* Display */}
          <div className="px-3 py-3 bg-slate-50 border-b">
            <div className="text-xs text-slate-500 font-mono break-all min-h-[18px]" data-testid="calc-expr">{expr || "0"}</div>
            <div className="flex items-end justify-between mt-1">
              <div className={`text-2xl font-bold font-mono ${err ? "text-red-500" : "text-slate-800"}`} data-testid="calc-result">
                = {result}
              </div>
              <button onClick={copyResult} title="Copy result (Ctrl+C)" className="text-slate-400 hover:text-emerald-600 p-1">
                <Copy className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

          {showKeys && (
            <div className="px-3 py-2 text-[11px] text-slate-600 bg-amber-50 border-b">
              <div><b>Alt+C</b> toggle · <b>Esc</b> close · <b>Enter</b> = · <b>Backspace</b> del · <b>Del</b> AC · <b>Ctrl+C</b> copy</div>
            </div>
          )}

          {showHistory && (
            <div className="max-h-40 overflow-auto border-b bg-white">
              {history.length === 0 ? (
                <div className="text-xs text-slate-400 text-center py-3">No history yet</div>
              ) : (
                history.map((h, i) => (
                  <div
                    key={i}
                    onClick={() => { setExpr(h.expr); setShowHistory(false); }}
                    className="px-3 py-1.5 text-xs hover:bg-emerald-50 cursor-pointer flex items-center justify-between border-b last:border-0"
                  >
                    <span className="font-mono text-slate-600 truncate mr-2">{h.expr}</span>
                    <span className="font-mono font-bold text-emerald-700">= {h.result}</span>
                  </div>
                ))
              )}
              {history.length > 0 && (
                <button onClick={() => setHistory([])} className="w-full text-[11px] text-red-500 py-1 hover:bg-red-50">
                  Clear history
                </button>
              )}
            </div>
          )}

          {/* Keypad */}
          <div className="p-2 grid grid-cols-4 gap-1.5 bg-white">
            <Btn cls="bg-red-100 hover:bg-red-200 text-red-700 col-span-2" onClick={clearAll} testid="calc-ac">AC</Btn>
            <Btn cls="bg-slate-100 hover:bg-slate-200 text-slate-700" onClick={backspace}><Delete className="w-4 h-4 mx-auto" /></Btn>
            <Btn cls="bg-amber-100 hover:bg-amber-200 text-amber-700" onClick={() => append("/")}>÷</Btn>

            <Btn cls="bg-slate-100 hover:bg-slate-200" onClick={() => append("7")} testid="calc-7">7</Btn>
            <Btn cls="bg-slate-100 hover:bg-slate-200" onClick={() => append("8")}>8</Btn>
            <Btn cls="bg-slate-100 hover:bg-slate-200" onClick={() => append("9")}>9</Btn>
            <Btn cls="bg-amber-100 hover:bg-amber-200 text-amber-700" onClick={() => append("*")}>×</Btn>

            <Btn cls="bg-slate-100 hover:bg-slate-200" onClick={() => append("4")}>4</Btn>
            <Btn cls="bg-slate-100 hover:bg-slate-200" onClick={() => append("5")}>5</Btn>
            <Btn cls="bg-slate-100 hover:bg-slate-200" onClick={() => append("6")}>6</Btn>
            <Btn cls="bg-amber-100 hover:bg-amber-200 text-amber-700" onClick={() => append("-")}>−</Btn>

            <Btn cls="bg-slate-100 hover:bg-slate-200" onClick={() => append("1")}>1</Btn>
            <Btn cls="bg-slate-100 hover:bg-slate-200" onClick={() => append("2")}>2</Btn>
            <Btn cls="bg-slate-100 hover:bg-slate-200" onClick={() => append("3")}>3</Btn>
            <Btn cls="bg-amber-100 hover:bg-amber-200 text-amber-700" onClick={() => append("+")}>+</Btn>

            <Btn cls="bg-slate-100 hover:bg-slate-200" onClick={() => append("%")}>%</Btn>
            <Btn cls="bg-slate-100 hover:bg-slate-200" onClick={() => append("0")} testid="calc-0">0</Btn>
            <Btn cls="bg-slate-100 hover:bg-slate-200" onClick={() => append(".")}>.</Btn>
            <Btn cls="bg-emerald-500 hover:bg-emerald-600 text-white" onClick={commit} testid="calc-equals">=</Btn>
          </div>
        </div>
      )}
    </>
  );
}
